import { type IReturn, nameOf } from "@servicestack/client";
import { useCallback, useContext, useMemo } from "react";
import ServiceStackClientContext from "src/contexts/api/ServiceStackClientContext";
import useSWR, { type SWRConfiguration, type SWRResponse } from "swr";

export type IHttpVerb = "get" | "post" | "put" | "delete";

const generateKey = <T>(request: IReturn<T>, httpVerb: IHttpVerb) => {
    const queryString = `SwrClient:${httpVerb}:${nameOf(request)}:${JSON.stringify(request)}`;

    return queryString;
};

const SwrClient = {
    api: <T>(
        httpVerb: IHttpVerb,
        fn?: () => IReturn<T>,
        configuration?: SWRConfiguration | undefined,
    ) => {
        const client = useContext(ServiceStackClientContext);

        const request = useMemo(() => {
            if (!fn) {
                return null;
            }

            return fn();
        }, [fn]);

        const key = useMemo(() => {
            if (!request) {
                return null;
            }

            return generateKey<T>(request, httpVerb);
        }, [request, httpVerb]);

        // @ts-ignore
        const fetcher = useCallback(
            (key: string) => {
                if (!request) {
                    return null;
                }

                return client[httpVerb](request);
            },
            [request, httpVerb],
        );

        return useSWR(key, fetcher, configuration) as SWRResponse<T, T>;
    },
};

const useSwrClient = () => {
    return SwrClient;
};

export default useSwrClient;
